import type { Filter, FilterClause } from "@garimpa/lib/facetFilter";
import { valueToArray } from "@garimpa/lib/valueToArray";

// Pure helpers for reading/writing a Filter tree by facet key. The faceted UI
// edits the flat "AND of per-facet clauses" shape: each top-level clause is one
// facet's selection (a term with OR'd values, or a range).
//
// A term clause's `f` may name several facets (`f: ["label", "company"]`) — e.g.
// the company→work link, which scopes to releases where the entity is the label
// OR the company. Such a composite is READ as selected under each of its facets
// (so the value renders in both dropdowns) but the widgets only ever WRITE a
// single-field clause. Nested boolean groups (or/and) are left untouched — they
// round-trip but aren't produced by the standard widgets.

type TermClause = { f: string | string[]; v: string | string[] };
type RangeClause = { f: string; r: [number | null, number | null] };

const isTerm = (clause: FilterClause): clause is TermClause => "v" in clause;
const isRange = (clause: FilterClause): clause is RangeClause => "r" in clause;

/** A term clause naming `key` among its (possibly multiple) fields. */
const termHasKey = (clause: FilterClause, key: string): boolean =>
  isTerm(clause) && valueToArray(clause.f).includes(key);

/** Any clause (term or range) targeting `key`. */
const matchesKey = (clause: FilterClause, key: string): boolean =>
  termHasKey(clause, key) || (isRange(clause) && clause.f === key);

/** Selected term values for a facet (OR within the facet). */
export function getFacetValues(filter: Filter, key: string): string[] {
  const clause = filter.find((c) => termHasKey(c, key));
  return clause && isTerm(clause) ? [...valueToArray(clause.v)] : [];
}

/** Replace the term clause for a key; empty values removes it. */
export function setFacetValues(filter: Filter, key: string, values: string[]): Filter {
  const rest = filter.filter((c) => !termHasKey(c, key));
  return values.length ? [...rest, { f: key, v: values }] : rest;
}

/** The numeric range selected for a key, or null. */
export function getFacetRange(filter: Filter, key: string): [number | null, number | null] | null {
  const clause = filter.find((c) => isRange(c) && c.f === key);
  return clause && isRange(clause) ? clause.r : null;
}

/** Replace the range clause for a key; an empty/[null,null] range removes it. */
export function setFacetRange(
  filter: Filter,
  key: string,
  range: [number | null, number | null] | null,
): Filter {
  const rest = filter.filter((c) => !(isRange(c) && c.f === key));
  if (!range || (range[0] == null && range[1] == null)) return rest;
  return [...rest, { f: key, r: range }];
}

/** Whether any clause targets this key (incl. multi-field composites). */
export function isFacetActive(filter: Filter, key: string): boolean {
  return filter.some((c) => matchesKey(c, key));
}

/** Drop every clause targeting a key (used by chip removal / clear). */
export function clearFacet(filter: Filter, key: string): Filter {
  return filter.filter((c) => !matchesKey(c, key));
}
